"use client";

import Link from "next/link";
import { Control, useWatch, useFormContext } from "react-hook-form";
import { CheckIcon, Info, Plug } from "lucide-react";
import { useMemo } from "react";
import { SpotSelectField } from "@/components/Form/SelectField";
import { Badge } from "@/components/shadcn/badge";
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "@/components/shadcn/hover-card";
import { SpotMarkdown } from "@/components/Spotflow/SpotMarkdown/SpotMarkdown";
import { NoxusAlert } from "@/components/UI/NoxusAlert";
import { NoxusFormLabel } from "@/components/Form/NoxusFormLabel";
import { cn } from "@/lib/utils";
import { useSpotUser } from "@/utils/spotUser";
import { linkedAccountsUrl } from "@/utils/activeGroup";
import { humanizeToolName } from "./MCPToolRow";
import { useMCPCredentials } from "./mcpCredentials";

// ---------------------------------------------------------------------------
// Credential picker — single-select
// ---------------------------------------------------------------------------

export function MCPCredentialPickerField({
  name,
  control,
  label,
  tooltip,
  placeholder,
  isDisabled,
}: {
  name: string;
  control: Control<Record<string, unknown>>;
  label: string;
  tooltip?: string | null;
  placeholder?: string | null;
  isDisabled?: boolean;
}) {
  const { spotGroup } = useSpotUser();
  const { credentials, isLoading } = useMCPCredentials();
  const currentValue = useWatch({ control, name }) as string | undefined;
  const options = useMemo(
    () =>
      credentials.map((c) => ({
        label: c.display_name,
        value: c.id,
        icon: c.integration_image || undefined,
        description: `${c.enabled_tools.length} tool${
          c.enabled_tools.length === 1 ? "" : "s"
        }`,
      })),
    [credentials]
  );

  // The node was configured with a credential_id, but that credential is
  // gone from the workspace (deleted, disconnected, or moved). Surface a
  // dedicated warning instead of the generic "no connections" message —
  // otherwise the user has no way to tell whether the workflow ever
  // worked or whether they just haven't connected anything yet.
  const hasOrphanedValue =
    !isLoading &&
    typeof currentValue === "string" &&
    currentValue.length > 0 &&
    !credentials.some((c) => c.id === currentValue);

  if (!isLoading && credentials.length === 0 && !hasOrphanedValue) {
    return (
      <div className="flex flex-col">
        <NoxusFormLabel label={label} tooltip={tooltip || ""} />
        <div className="flex items-center gap-2 text-xs text-muted-foreground border rounded-md p-3">
          <Plug className="size-4 shrink-0" />
          <span>
            No MCP connections. Add one from the{" "}
            <Link
              href={linkedAccountsUrl(spotGroup.id)}
              target="_blank"
              rel="noopener noreferrer"
              className="font-medium text-primary underline hover:no-underline"
            >
              Integrations page
            </Link>
            .
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2">
      {hasOrphanedValue && (
        <NoxusAlert
          variant="warning"
          size="xs"
          direction="vertical"
          title="MCP connection unavailable"
          description={
            <>
              The MCP server this node was configured with isn't available in
              this workspace anymore (it may have been removed or disconnected).
              Pick another connection
              {credentials.length === 0 ? " after connecting one" : ""} to get
              this node working again.
            </>
          }
        />
      )}
      <SpotSelectField
        name={name}
        control={control as unknown as Control<Record<string, unknown>>}
        label={label}
        tooltip={tooltip || undefined}
        placeholder={placeholder || "Pick an integration"}
        options={options}
        isLoading={isLoading}
        isDisabled={isDisabled}
        customItemContent={(option, selected) => {
          const inList = selected !== undefined;
          return (
            <div className="flex items-center gap-1.5 w-full min-w-0">
              {inList && (
                <CheckIcon
                  className={cn(
                    "h-4 w-4 shrink-0",
                    selected ? "opacity-100" : "opacity-0"
                  )}
                />
              )}
              {option?.icon && typeof option.icon === "string" && (
                <img
                  src={option.icon}
                  alt={option.label}
                  className="w-5 h-5 shrink-0 rounded-sm"
                />
              )}
              <span className="truncate">{option?.label}</span>
              <Badge variant="outline">MCP</Badge>
              {option?.description && (
                <span className="text-muted-foreground shrink-0">
                  · {option.description}
                </span>
              )}
            </div>
          );
        }}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tool picker — reads credential_id from a sibling form field
// ---------------------------------------------------------------------------

export function MCPToolPickerField({
  name,
  control,
  label,
  tooltip,
  placeholder,
  credentialField,
  isDisabled,
}: {
  name: string;
  control: Control<Record<string, unknown>>;
  label: string;
  tooltip?: string | null;
  placeholder?: string | null;
  credentialField: string;
  isDisabled?: boolean;
}) {
  // The credential_id field is a sibling in the same config form; watching
  // it makes the tool list react the moment the user changes credentials.
  const credentialId = useWatch({ control, name: credentialField }) as
    | string
    | undefined;
  const currentTool = useWatch({ control, name }) as string | undefined;
  const { setValue } = useFormContext();

  // Switching tools must zero out `arguments`: the rows are seeded per-tool
  // (get_config), so a stale key from the previous tool would otherwise linger
  // and get rejected by strict servers (`additionalProperties: false`). Fires
  // only on user selection (SpotSelectField.onChange), never on form hydration,
  // so saved arguments survive a reload. The sibling path mirrors how the
  // credential field is derived in BaseConfig (replace the trailing segment).
  const clearArgumentsOnToolChange = (value: string) => {
    if (value === (currentTool ?? "")) return;
    setValue(
      name.replace(/[^.]+$/, "arguments"),
      {},
      {
        shouldDirty: true,
        shouldTouch: true,
      }
    );
  };

  const { credentials, isLoading } = useMCPCredentials();
  const credential = useMemo(() => {
    if (!credentialId) return undefined;
    return credentials.find((c) => c.id === credentialId);
  }, [credentials, credentialId]);

  const options = useMemo(() => {
    if (!credential) return [];
    return credential.enabled_tools
      .map((n) => {
        const tool = credential.discovered_tools.find((t) => t.name === n);
        return {
          label: n,
          value: n,
          description: tool?.description || undefined,
        };
      })
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [credential]);

  if (!credentialId) {
    return (
      <div className="flex flex-col">
        <NoxusFormLabel label={label} tooltip={tooltip || ""} />
        <p className="text-xs text-muted-foreground">
          Pick an integration first.
        </p>
      </div>
    );
  }

  if (!isLoading && credential && options.length === 0) {
    return (
      <div className="flex flex-col gap-2">
        <NoxusFormLabel label={label} tooltip={tooltip || ""} />
        <p className="text-xs text-muted-foreground">
          This integration has no enabled tools. Enable some on the credential's
          Tools tab.
        </p>
      </div>
    );
  }

  return (
    <SpotSelectField
      name={name}
      control={control as unknown as Control<Record<string, unknown>>}
      label={label}
      tooltip={tooltip || undefined}
      placeholder={placeholder || "Pick a tool"}
      options={options}
      isLoading={isLoading}
      isDisabled={isDisabled}
      onChange={clearArgumentsOnToolChange}
      customItemContent={(option, selected) => {
        if (!option) return null;
        const inList = selected !== undefined;
        const slug = String(option.value);
        const description =
          typeof option.description === "string" ? option.description : "";
        return (
          <div className="flex items-center gap-1.5 w-full min-w-0">
            {inList && (
              <CheckIcon
                className={cn(
                  "h-4 w-4 shrink-0",
                  selected ? "opacity-100" : "opacity-0"
                )}
              />
            )}
            <span className="truncate">{humanizeToolName(slug)}</span>
            <span className="text-xs font-mono text-muted-foreground shrink-0">
              {slug}
            </span>
            {description && (
              <HoverCard openDelay={150} closeDelay={100}>
                <HoverCardTrigger asChild>
                  <button
                    type="button"
                    className="text-muted-foreground hover:text-foreground shrink-0"
                    aria-label={`Description for ${slug}`}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Info className="size-3.5" />
                  </button>
                </HoverCardTrigger>
                <HoverCardContent
                  side="top"
                  align="start"
                  className="w-[28rem] max-h-80 p-0 overflow-hidden flex flex-col"
                >
                  <div className="px-4 pt-3 pb-2 text-xs font-mono text-muted-foreground border-b shrink-0">
                    {slug}
                  </div>
                  <div className="px-4 py-3 text-xs overflow-y-auto overscroll-contain min-h-0">
                    <SpotMarkdown markdown={description} />
                  </div>
                </HoverCardContent>
              </HoverCard>
            )}
          </div>
        );
      }}
    />
  );
}
